import { useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Users, ArrowLeft, Loader2, Pencil, X, Check, Plus, UserPlus, Star, Trash2,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Skeleton } from '@/components/ui/skeleton';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Switch } from '@/components/ui/switch';
import { Label } from '@/components/ui/label';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '@/components/ui/select';
import { MarkdownRenderer } from '../components/MarkdownRenderer';
import {
  useSeat,
  useSeats,
  useUpdateSeat,
  useAssignHolder,
  useRemoveHolder,
  useSetPrimaryHolder,
} from '../api/seats';
import { useEmployees } from '@/features/teams/api/employees';
import type { Seat } from '../types';

function holderName(h: Seat['holders'][number]): string {
  return [h.firstname, h.lastname].filter(Boolean).join(' ') || h.email || 'Unknown';
}

// =============================================================================
// Inline editors
// =============================================================================

function InlineText({
  value, onSave, multiline = false, placeholder = 'Click to edit',
}: {
  value: string | null;
  onSave: (v: string | null) => Promise<void>;
  multiline?: boolean;
  placeholder?: string;
}) {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(value ?? '');
  const [saving, setSaving] = useState(false);

  const commit = async () => {
    setSaving(true);
    try { await onSave(draft.trim() || null); setEditing(false); } finally { setSaving(false); }
  };

  if (editing) {
    return (
      <div className="space-y-2">
        {multiline ? (
          <Textarea rows={8} value={draft} onChange={(e) => setDraft(e.target.value)} autoFocus />
        ) : (
          <Input value={draft} onChange={(e) => setDraft(e.target.value)} autoFocus />
        )}
        <div className="flex gap-2">
          <Button size="sm" onClick={commit} disabled={saving}>
            {saving ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Check className="h-3.5 w-3.5" />}
          </Button>
          <Button size="sm" variant="ghost" onClick={() => { setDraft(value ?? ''); setEditing(false); }}>
            <X className="h-3.5 w-3.5" />
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div
      onClick={() => setEditing(true)}
      className="cursor-pointer hover:bg-muted/50 rounded px-2 py-1 -mx-2 -my-1 group relative"
    >
      {value ? (
        multiline ? <MarkdownRenderer>{value}</MarkdownRenderer> : <span>{value}</span>
      ) : (
        <span className="text-muted-foreground italic">{placeholder}</span>
      )}
      <Pencil className="absolute top-1 right-1 h-3 w-3 text-muted-foreground opacity-0 group-hover:opacity-100" />
    </div>
  );
}

// =============================================================================
// Holder list
// =============================================================================

function HolderList({ seat }: { seat: Seat }) {
  const assign = useAssignHolder();
  const remove = useRemoveHolder();
  const setPrimary = useSetPrimaryHolder();
  const { data: employeesData } = useEmployees();
  const employees = employeesData?.employees ?? [];

  const [adding, setAdding] = useState(false);
  const [selectedUser, setSelectedUser] = useState<string>('');
  const [territory, setTerritory] = useState('');

  const holderUserIds = new Set(seat.holders.map((h) => h.user_id));
  const availableEmployees = employees.filter((e) => !holderUserIds.has(e.id));

  const addHolder = async () => {
    if (!selectedUser) return;
    await assign.mutateAsync({
      seatId: seat.id,
      input: {
        user_id: selectedUser,
        territory: territory || null,
        is_primary: seat.holders.length === 0,
      },
    });
    setAdding(false);
    setSelectedUser('');
    setTerritory('');
  };

  return (
    <Card>
      <CardHeader className="flex flex-row items-center justify-between pb-3">
        <CardTitle className="text-base">
          Holders {seat.holders.length > 0 && <Badge variant="secondary" className="ml-2">{seat.holders.length}</Badge>}
        </CardTitle>
        {(seat.allow_multiple || seat.holders.length === 0) && !adding && (
          <Button size="sm" variant="outline" onClick={() => setAdding(true)}>
            <UserPlus className="h-3.5 w-3.5 mr-1" /> Add holder
          </Button>
        )}
      </CardHeader>
      <CardContent className="space-y-3">
        {adding && (
          <div className="border rounded-lg p-3 space-y-2 bg-muted/20">
            <Select value={selectedUser} onValueChange={setSelectedUser}>
              <SelectTrigger>
                <SelectValue placeholder="Select employee…" />
              </SelectTrigger>
              <SelectContent>
                {availableEmployees.map((e) => (
                  <SelectItem key={e.id} value={e.id}>
                    {[e.firstname, e.lastname].filter(Boolean).join(' ') || e.email}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            {seat.allow_multiple && (
              <Input
                placeholder="Territory (optional)"
                value={territory}
                onChange={(e) => setTerritory(e.target.value)}
              />
            )}
            <div className="flex gap-2">
              <Button size="sm" onClick={addHolder} disabled={!selectedUser || assign.isPending}>
                {assign.isPending ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : 'Assign'}
              </Button>
              <Button size="sm" variant="ghost" onClick={() => { setAdding(false); setSelectedUser(''); setTerritory(''); }}>
                Cancel
              </Button>
            </div>
          </div>
        )}

        {seat.holders.length === 0 ? (
          <div className="text-sm text-muted-foreground italic py-4 text-center">Vacant</div>
        ) : (
          <div className="space-y-2">
            {seat.holders.map((h) => (
              <div key={h.id} className="flex items-center gap-3 border rounded-lg px-3 py-2 hover:bg-muted/30">
                {h.profile_photo_url ? (
                  <img src={h.profile_photo_url} alt="" className="h-8 w-8 rounded-full object-cover" />
                ) : (
                  <div className="h-8 w-8 rounded-full bg-primary/10 flex items-center justify-center text-xs font-semibold text-primary">
                    {holderName(h).split(' ').map((p) => p[0]).join('').toUpperCase().slice(0, 2)}
                  </div>
                )}
                <div className="flex-1 min-w-0">
                  <div className="text-sm font-medium truncate">{holderName(h)}</div>
                  <div className="text-xs text-muted-foreground truncate">
                    {h.email}
                    {h.territory && <> · {h.territory}</>}
                  </div>
                </div>
                {h.is_primary && (
                  <Badge variant="secondary" className="bg-yellow-100 text-yellow-800 text-[10px]">
                    <Star className="h-2.5 w-2.5 mr-0.5 fill-current" /> Primary
                  </Badge>
                )}
                {!h.is_primary && (
                  <Button
                    size="sm"
                    variant="ghost"
                    onClick={() => setPrimary.mutate({ seatId: seat.id, userId: h.user_id })}
                    title="Set as primary seat for this user"
                  >
                    <Star className="h-3.5 w-3.5" />
                  </Button>
                )}
                <Button
                  size="sm"
                  variant="ghost"
                  onClick={() => {
                    if (confirm(`Remove ${holderName(h)} from ${seat.title}?`)) {
                      remove.mutate({ seatId: seat.id, userId: h.user_id });
                    }
                  }}
                >
                  <Trash2 className="h-3.5 w-3.5 text-red-500" />
                </Button>
              </div>
            ))}
          </div>
        )}
      </CardContent>
    </Card>
  );
}

// =============================================================================
// Parent seat selector
// =============================================================================

function ParentSeatField({ seat }: { seat: Seat }) {
  const update = useUpdateSeat();
  const { data } = useSeats();
  const allSeats = data?.seats ?? [];
  // Cannot parent to self or any descendant (skip the self-cycle prevention, backend also guards)
  const options = allSeats.filter((s) => s.id !== seat.id);

  const currentParent = allSeats.find((s) => s.id === seat.parent_seat_id);

  const setParent = async (value: string) => {
    await update.mutateAsync({
      id: seat.id,
      input: { parent_seat_id: value === '__none__' ? null : value },
    });
  };

  return (
    <div>
      <Label className="text-xs uppercase text-muted-foreground">Reports to</Label>
      <Select value={seat.parent_seat_id ?? '__none__'} onValueChange={setParent}>
        <SelectTrigger className="mt-1">
          <SelectValue>{currentParent?.title ?? 'None (root seat)'}</SelectValue>
        </SelectTrigger>
        <SelectContent>
          <SelectItem value="__none__"><em>None (root seat)</em></SelectItem>
          {options.map((s) => (
            <SelectItem key={s.id} value={s.id}>{s.title}</SelectItem>
          ))}
        </SelectContent>
      </Select>
    </div>
  );
}

// =============================================================================
// Decision rights editor
// =============================================================================

function DecisionRightsEditor({ seat }: { seat: Seat }) {
  const update = useUpdateSeat();
  const [adding, setAdding] = useState(false);
  const [draft, setDraft] = useState('');
  const [draftType, setDraftType] = useState<'own' | 'recommend' | 'input' | 'informed'>('own');

  const typeColors = {
    own: 'bg-green-100 text-green-800',
    recommend: 'bg-blue-100 text-blue-800',
    input: 'bg-purple-100 text-purple-800',
    informed: 'bg-zinc-100 text-zinc-700',
  };

  const save = (rights: typeof seat.decision_rights) =>
    update.mutateAsync({ id: seat.id, input: { decision_rights: rights } });

  const add = async () => {
    if (!draft.trim()) return;
    await save([...seat.decision_rights, { decision: draft.trim(), type: draftType }]);
    setDraft('');
    setAdding(false);
  };

  const remove = (idx: number) => save(seat.decision_rights.filter((_, i) => i !== idx));

  return (
    <Card>
      <CardHeader className="flex flex-row items-center justify-between pb-3">
        <CardTitle className="text-base">Decision Rights</CardTitle>
        {!adding && (
          <Button size="sm" variant="outline" onClick={() => setAdding(true)}>
            <Plus className="h-3.5 w-3.5 mr-1" /> Add
          </Button>
        )}
      </CardHeader>
      <CardContent className="space-y-2">
        {adding && (
          <div className="flex gap-2 items-center border rounded-lg p-2 bg-muted/20">
            <Input
              placeholder="Decision…"
              value={draft}
              onChange={(e) => setDraft(e.target.value)}
              autoFocus
              className="flex-1"
            />
            <Select value={draftType} onValueChange={(v) => setDraftType(v as typeof draftType)}>
              <SelectTrigger className="w-32"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="own">Own</SelectItem>
                <SelectItem value="recommend">Recommend</SelectItem>
                <SelectItem value="input">Input</SelectItem>
                <SelectItem value="informed">Informed</SelectItem>
              </SelectContent>
            </Select>
            <Button size="sm" onClick={add}>Add</Button>
            <Button size="sm" variant="ghost" onClick={() => { setAdding(false); setDraft(''); }}><X className="h-3.5 w-3.5" /></Button>
          </div>
        )}
        {seat.decision_rights.length === 0 && !adding ? (
          <div className="text-sm text-muted-foreground italic py-2 text-center">No decision rights defined</div>
        ) : (
          seat.decision_rights.map((dr, idx) => (
            <div key={idx} className="flex items-center gap-2 group">
              <Badge variant="secondary" className={`${typeColors[dr.type]} text-[10px] w-20 justify-center`}>
                {dr.type}
              </Badge>
              <span className="text-sm flex-1">{dr.decision}</span>
              <Button size="sm" variant="ghost" className="opacity-0 group-hover:opacity-100" onClick={() => remove(idx)}>
                <Trash2 className="h-3 w-3 text-red-500" />
              </Button>
            </div>
          ))
        )}
      </CardContent>
    </Card>
  );
}

// =============================================================================
// Main page
// =============================================================================

export function SeatDetailPage() {
  const { seatId } = useParams<{ seatId: string }>();
  const navigate = useNavigate();
  const { data: seat, isLoading } = useSeat(seatId ?? null);
  const update = useUpdateSeat();

  if (isLoading) {
    return (
      <div className="p-6 space-y-4">
        <Skeleton className="h-8 w-64" />
        <Skeleton className="h-64 w-full" />
      </div>
    );
  }

  if (!seat) {
    return (
      <div className="flex flex-col items-center justify-center min-h-[50vh] gap-4 p-6">
        <Users className="h-12 w-12 text-muted-foreground opacity-40" />
        <h2 className="text-lg font-semibold">Seat not found</h2>
        <Button variant="outline" onClick={() => navigate('/os/accountability?tab=seats')}>
          Back to Seats
        </Button>
      </div>
    );
  }

  const saveField = (field: keyof Seat) => async (value: string | null) => {
    await update.mutateAsync({ id: seat.id, input: { [field]: value } });
  };

  return (
    <div className="flex flex-col gap-4 p-6 max-w-6xl mx-auto">
      <div className="flex items-center gap-3 flex-wrap">
        <Button variant="ghost" size="sm" onClick={() => navigate('/os/accountability?tab=seats')}>
          <ArrowLeft className="h-4 w-4 mr-1" /> Seats
        </Button>
        <h1 className="text-2xl font-semibold">{seat.title}</h1>
        {seat.teams.map((t) => (
          <Badge key={t.id} variant="outline">{t.name}</Badge>
        ))}
        {seat.allow_multiple && (
          <Badge variant="secondary" className="bg-amber-100 text-amber-800">Multi-holder</Badge>
        )}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-5">
        {/* Left: metadata */}
        <div className="lg:col-span-1 space-y-4">
          <Card>
            <CardHeader className="pb-3"><CardTitle className="text-base">Seat</CardTitle></CardHeader>
            <CardContent className="space-y-3">
              <div>
                <Label className="text-xs uppercase text-muted-foreground">Title</Label>
                <InlineText value={seat.title} onSave={saveField('title')} />
              </div>
              <div>
                <Label className="text-xs uppercase text-muted-foreground">Slug</Label>
                <InlineText value={seat.slug} onSave={saveField('slug')} />
              </div>
              <ParentSeatField seat={seat} />
              <div className="flex items-center justify-between pt-2 border-t">
                <Label>Allow multiple holders</Label>
                <Switch
                  checked={seat.allow_multiple}
                  onCheckedChange={(checked) =>
                    update.mutate({ id: seat.id, input: { allow_multiple: checked } })
                  }
                />
              </div>
            </CardContent>
          </Card>

          <HolderList seat={seat} />
        </div>

        {/* Right: description + decision rights */}
        <div className="lg:col-span-2 space-y-4">
          <Card>
            <CardHeader className="pb-3"><CardTitle className="text-base">Role Description</CardTitle></CardHeader>
            <CardContent>
              <InlineText
                value={seat.description}
                onSave={saveField('description')}
                multiline
                placeholder="Describe the mission and responsibilities…"
              />
            </CardContent>
          </Card>

          <DecisionRightsEditor seat={seat} />
        </div>
      </div>
    </div>
  );
}
